import { createAsyncThunk } from '@reduxjs/toolkit';
import ApiService from '../../../infrastructure/services/api/services';
import { apiConfig } from '../../../application/configurations/apiConfig';
import { ApiConfig } from '../../../application/configurations/IConfig';
import { toggleLoading } from '../../components/pageLoader/loaderReducer';
import { appConstants } from '../../../application/configurations/constants';
import { UtilityService } from '../../../infrastructure/services/utils';
import { resultsConfigurations } from './config';
import {
  enableFlagForSuggestedOrRecentSearch,
  setBestBetFlag,
  setInsightsFlag,
  clearInsightsLeads
} from './searchReducer';
import { store } from '../../../application/store';
import AnalyticsEvents from '../../../infrastructure/services/analytics/analyticsUseCase';
import { AdobeAnalyticsEvents } from '../../../infrastructure/services/analytics/adobeAnalyticsUseCase';
import { setHomePageSearchFlag } from './searchReducer';
import { PeopleSortConfig } from '../../components/sort/config';
import { resultsConstants } from './constants';
import { cardConfig } from '../../components/cards/config';
import envConfig, { isLocalOrDevEnv } from '../../../application/configurations/envConfig';
import JsonData from '../../../application/configurations/refiner.json';
import HybridSearchJsonData from '../../../application/configurations/HybridSearchRefinersSequence.json';
import GeographyData from '../../../application/configurations/geographyTaxonomy.json';

/**
 * class for processing async functions based on actions
 */
class Search {
  private searchApiConfig: ApiConfig;

  private hybridSearchApiConfig: ApiConfig;

  private isSemanticSearchEnabled: boolean = false;

  constructor() {
    this.searchApiConfig = { ...apiConfig.search };
    this.hybridSearchApiConfig = { ...apiConfig.HybridSearch };
  }

  //Call search and hybrid search API based on toggle
  private async getSearchConfig(searchTerm: string, vertical: string) {

    // If toggle feature is enabled, set isSemanticSearchEnabled based on user's searchMode preference from API
    if (envConfig.isToggleAISearchEnabled) {
      this.isSemanticSearchEnabled = store.getState().search.searchMode === 'Semantic';

      // If forceKeywordSearchNews or forceKeywordSearchPeople, force Keyword search for People and News verticals
      if ((envConfig.forceKeywordSearchNews && vertical === appConstants.verticals.News) || (envConfig.forceKeywordSearchPeople && vertical === appConstants.verticals.People)) {
        this.isSemanticSearchEnabled = false;
      }
    } else {
      this.isSemanticSearchEnabled = false;
    }

    return this.hybridSearchApiConfig;
  }

  /**
   * async method with redux store middleware Thunk wrapper
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {object} request the request object containing search query term & an optional start row.
   * @return  {object} return api response
   */
  doSearch = createAsyncThunk(
    'search',
    async (
      request: {
        searchTerm: string;
        vertical: string;
        filterByDate: number;
        accounts: Array<Object>;
        refinersClicked: any;
        flagForContentAge: boolean;
        flagForClear: boolean;
        startRow: number;
        homepageSearch: boolean;
        sortByValue: {
          name: string;
          Value: string;
          Direction: number;
          SortType: number;
        };
        refinerValues: any;
        clearAll: boolean;
      },
      thunkApi
    ) => {
      // Check if we have the necessary authentication
      if (!request.accounts || request.accounts.length === 0) {
        thunkApi.dispatch(toggleLoading(false));

        return {
          results: [],
          resultCount: 0,
          profilePhotoCollection: [],
          refiners: [],
          latency: 0,
          nestedRefiners: []
        };
      }

      // dispatching action to show loader
      thunkApi.dispatch(toggleLoading(true));
      let responseData: any = [];
      let resultCount: number = 0;
      let peopleAccountId: Array<string | undefined> = [];
      let profilePhotoCollection: any = [];
      let refinedVerticals: any = [];
      let latency: number = 0;
      let nestedRefinersFinal: any = [];
      const filterObject: any = JsonData.filter(
        (item) => item.vertical === request.vertical
      );
      const HybridSearchSequence: any = HybridSearchJsonData.filter(
        (item) => item.vertical === request.vertical
      )[0];
      let resultsLimit: any = resultsConfigurations.resultsPerPage.filter(
        (option) => option.vertical === request.vertical
      );
      let noOfItemsPerPage = JSON.parse(
        localStorage.getItem(appConstants.noOfItemsPerPage) || '{}'
      );
      let itemsToSetPerPage: number = noOfItemsPerPage
        ? noOfItemsPerPage
        : appConstants.defaultItemsPerPage;
      let modelObj = UtilityService.getRequestModel(request.vertical);
      const searchConfig = {
        ...await this.getSearchConfig(request.searchTerm, request.vertical)
      };
      searchConfig.accountInfo = request.accounts;
      searchConfig.data = {
        ...searchConfig.data,
          Query: request.searchTerm.replace(/"/g, ''),
        verticals: request.vertical,
        Refiners:
          filterObject && filterObject.length > 0
            ? Object.keys(filterObject[0])
            : [appConstants.RefinerName],
        Size:
          request.startRow > resultsLimit[0].count - itemsToSetPerPage
            ? resultsLimit[0].count - request.startRow
            : itemsToSetPerPage,
        From: request.startRow ? request.startRow : 0,
        ReturnFields: modelObj ? Object.keys(modelObj) : [],
        Sort: [],
        SearchType: this.isSemanticSearchEnabled
          ? appConstants.searchType.Semantic
          : appConstants.searchType.Keyword
      };
      // Initialize Filters
      searchConfig.data.Filters = {
        Condition: 'AND',
        Rules: []
      };

      // filter to show only published content in case of documents vertical.
      searchConfig.data.Filters.Rules.push({
        Field: 'ESC_EYCONTENTSTATUS',
        Type: 'String',
        Operator: ':',
        Value: 'Publish'
      });

      // filter search by Date of content age API call condition
      if (request.filterByDate && modelObj.contentAge) {
        searchConfig.data.Filters.Rules.push({
          Field: modelObj.contentAge,
          Type: 'Date',
          Operator: 'Range',
          Value: `${UtilityService.getMonthDates(
            request.filterByDate
          )};${new Date().toISOString()}`
        });
      }
      if (
        request.vertical === appConstants.verticals.All ||
        request.vertical === appConstants.verticals.News
      ) {
        searchConfig.data.PersonalFilters = {
          Condition: 'AND',
          Rules: []
        };

        let geographyFilters: string[] = [];
        let userInfo = null;
        try {
          userInfo = sessionStorage.UserInfo
            ? JSON.parse(sessionStorage.UserInfo)
            : null;
        } catch (parseError) {
          userInfo = null;
        }

        if (userInfo && userInfo.EYCountry) {

          const path = UtilityService.findGeographyPath(
            GeographyData.children,
            userInfo.EYCountry,
            []
          );

          if (path) {
            geographyFilters.push(...[...path].reverse());
          } else {
            geographyFilters.push(userInfo.EYCountry);
          }
        }

        geographyFilters.push('Global');
        
        // Check if there are multiple geography filters
        if (geographyFilters.length > 1) {
          searchConfig.data.PersonalFilters.Rules.push({
            Field: 'ESC_GEOGRAPHICAPPLICABILITY',
            Type: 'String',
            Operator: ':',
            Values: geographyFilters
          });
          searchConfig.data.PersonalFilters.Rules.push({
            Field: 'ESC_GEOGRAPHICAPPLICABILITY_ALT_LABEL',
            Type: 'String',
            Operator: ':',
            Values: geographyFilters
          });
        } else {
          searchConfig.data.PersonalFilters.Rules.push({
            Field: 'ESC_GEOGRAPHICAPPLICABILITY',
            Type: 'String',
            Operator: ':',
            Value: geographyFilters[0]
          });
          searchConfig.data.PersonalFilters.Rules.push({
            Field: 'ESC_GEOGRAPHICAPPLICABILITY_ALT_LABEL',
            Type: 'String',
            Operator: ':',
            Value: geographyFilters[0]
          });
        }
      }

      // Refiners API call condition
      if (
        request.refinerValues !== undefined &&
        request.refinerValues.length > 0
      ) {
        if (request.vertical === appConstants.verticals.People) {
          searchConfig.data.Filters = {
            Condition: 'AND',
            Rules: []
          };
        }
        request.refinerValues.forEach((item: any) => {
          if (
            item[Object.keys(item)[0]] !== undefined &&
            item[Object.keys(item)[0]].length > 0
          ) {
            // Condition to check if selected refiner is more than one value
            UtilityService.selectedRefiners(
              item,
              searchConfig.data.Filters.Rules
            );
          }
        });
      }

      //  sort by filter API call condition
      if (request.sortByValue && request.sortByValue !== undefined) {
        searchConfig.data.Sort.push({
          FieldName: request.sortByValue.Value,
          Ascending: request.sortByValue.Direction == 1 ? false : true
        });
        if (
          request.vertical === appConstants.verticals.People &&
          (request.sortByValue.Value ===
            PeopleSortConfig.filterOptions[1].Value ||
            request.sortByValue.Value ===
            PeopleSortConfig.filterOptions[3].Value)
        ) {
          searchConfig.data.Sort.push({
            FieldName:
              request.sortByValue.Value ===
                PeopleSortConfig.filterOptions[1].Value
                ? PeopleSortConfig.filterOptions[3].Value
                : PeopleSortConfig.filterOptions[1].Value,
            Ascending: request.sortByValue.Direction == 1 ? false : true
          });
        }
      }

      if (request.vertical === appConstants.verticals.All) {
        searchConfig.data.Filters.Rules.push({
          Field: appConstants.ElasticFields.vertical,
          Type: 'String',
          Operator: ':',
          Values: [
            appConstants.verticals.Credentials,
            appConstants.verticals.Data,
            appConstants.verticals.Documents,
            appConstants.verticals.News,
            appConstants.verticals.People,
            appConstants.verticals.Videos,
            appConstants.nonVerticals.Intranet
          ]
        });
      } else {
        let verticalText =
          request.vertical.toLowerCase() === appConstants.adobeAnalytics.browseConfigs.solutions.toLocaleLowerCase()
            ? appConstants.verticals.Documents
            : request.vertical;
        verticalText =
          request.vertical.toLowerCase() === appConstants.nonVerticals.PolicyFinder.toLocaleLowerCase()
            ? appConstants.verticals.Documents
            : verticalText;
        verticalText =
          request.vertical.toLowerCase() === appConstants.adobeAnalytics.browseConfigs.proposals.toLocaleLowerCase()
            ? appConstants.verticals.Documents
            : verticalText;
        verticalText =
          request.vertical.toLowerCase() === appConstants.adobeAnalytics.browseConfigs.credentials.toLocaleLowerCase()
            ? appConstants.verticals.Credentials
            : verticalText;
        verticalText =
          request.vertical.toLowerCase() === appConstants.EYPKC.toLocaleLowerCase()
            ? appConstants.verticals.Documents
            : verticalText;
        searchConfig.data.Filters.Rules.push({
          Field: appConstants.ElasticFields.vertical,
          Type: 'String',
          Operator: ':',
          Value: verticalText
        });
      }
      await ApiService.requestApi([searchConfig])
        .then(
          (response: { [key: string]: any }) => {
            // Variable for backend failure counts i.e., isError property count in the actual API response object
            let errorCount: number = 0;
            if (response && response[0] && response[0].result.relevantResults) {
              try {
                // Refiners
                // First Level
                let level1Transformation = UtilityService.convertRefinersLevel1(
                  JSON.parse(response[0].result.refinementResultsJSON)
                );

                // Second Level
                level1Transformation.map((refine: any) => {
                  let object1: any = {};
                  //Condition to check if the Refiner is from AEM using path keyword.
                  object1 =
                    refine.title.indexOf('UFPATH') > -1
                      ? {
                        title: refine.title,
                        refId: refine.refId,
                        ID: Object.keys(HybridSearchSequence).find(
                          (k) => HybridSearchSequence[k] === refine.title
                        ),
                        source: UtilityService.convertRefinersLevel2({
                          RefinementResults: refine.source.slice(1)
                        })
                      }
                      : {
                        title: refine.title,
                        refId: refine.refId,
                        ID: Object.keys(HybridSearchSequence).find(
                          (k) => HybridSearchSequence[k] === refine.title
                        ),
                        source: UtilityService.convertRefinersLevel2({
                          RefinementResults: refine.source
                        })
                      };

                  object1.title =
                    filterObject[0] &&
                      filterObject[0][object1.title] !== undefined &&
                      object1.title !== 'vertical'
                      ? filterObject[0][object1.title]
                      : object1.title;

                  nestedRefinersFinal.push(object1);
                });

                // Dont remove this console until we implement Refiners.
                // Sort refiners based on the order defined in HybridSearchRefinersSequence.json
                nestedRefinersFinal = [...nestedRefinersFinal].sort(
                  (a, b) => {
                    if (a.ID === undefined) return 1; // Place undefined at the end

                    if (b.ID === undefined) return -1; // Place undefined at the end

                    return a.ID - b.ID; // Sort numerically
                  }
                );
              } catch (error) {
                if (isLocalOrDevEnv) {
                  console.log(error);
                }
              }
              // Refiners logics ends here.

              response[0].result.relevantResults.forEach((record: any) => {
                let federatorData = JSON.parse(record.FederatorMetadata);
                if (federatorData && federatorData.length > 0) {
                  let backendName = federatorData.find((obj: any) => {
                    return obj.Key === 'FederatorBackend';
                  }).Value;
                  switch (backendName) {
                    case appConstants.backendDataSources.people:
                      record.vertical = appConstants.verticals.People;
                      peopleAccountId.push(record.accountId);
                      break;
                    case appConstants.backendDataSources.people_stg:
                      record.vertical = appConstants.verticals.People;
                      peopleAccountId.push(record.accountId);
                      break;
                    case appConstants.elasticIndexNames.discoverpeople:
                    case appConstants.elasticIndexNames.discoverpeople_qa:
                    case appConstants.elasticIndexNames.discoverpeople_ua:
                      record.vertical = appConstants.verticals.People;
                          peopleAccountId.push(record.accountId);
                      break;
                    default:
                      break;
                  }

                  responseData.push(
                    UtilityService.getResponseModel(record.vertical, record)
                  );
                } else {
                  // Increasing the backend failure count when the FederatorMetadata array is empty in the results.
                  errorCount++;
                }
              });
              // Setting up the Results Found count by decreasing with the backend failure count(errorCount).
              // This will fix for the backend failure scenarios i.e., when the backend returns any error in the response.
              resultCount =
                responseData.length > 0
                  ? response[0].result.totalRelevantResults - errorCount
                  : responseData.length;
              refinedVerticals = response[0].result.refinementResults;
              latency = response[0].result.totalTime;
              const validPeopleAccountIds = peopleAccountId.filter((id) => id);
              if (validPeopleAccountIds.length) {
                thunkApi.dispatch(
                  this.getUserProfilePic({
                    peopleAccountIds: validPeopleAccountIds,
                    accounts: request.accounts
                  })
                );
              }
            }
          },
          (error) => {
            throw new Error(error);
          }
        )
        .finally(() => {
          // Calling the Analytics Events
          this.callAnalytics(request, resultCount);

          // injecting page load adobe Analytics Events
          AdobeAnalyticsEvents.injectAdobePageLoadEvents(
            request.searchTerm,
            resultCount,
            store.getState().general.userInfo,
            request.vertical,
            false
          );

          thunkApi.dispatch(setHomePageSearchFlag(false));
          thunkApi.dispatch(enableFlagForSuggestedOrRecentSearch(''));
        });

      return {
        results: responseData,
        resultCount: resultCount,
        profilePhotoCollection: profilePhotoCollection,
        refiners: refinedVerticals,
        latency: latency,
        nestedRefiners: nestedRefinersFinal,
        isSemanticSearchEnabled: this.isSemanticSearchEnabled
      };
    }
  );

  /**
   * Method to trigger analytics actions
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * @param  {object} request the request object containing search query term & an optional start row.
   * @param  {number} resultCount search result count
   */
  callAnalytics = (
    request: {
      searchTerm: string;
      vertical: string;
      filterByDate?: number;
      accounts: Array<Object>;
      startRow?: number | undefined;
      homepageSearch: boolean;
      refinersClicked?: Array<Object>;
      flagForContentAge?: boolean;
      flagForClear?: boolean;
      clearAll?: boolean;
    },
    resultCount: number | undefined
  ) => {
    const flagForSuggestedOrRecentSearch =
      store.getState().search.flagForSuggestedOrRecentSearch;

    if (flagForSuggestedOrRecentSearch !== '') {
      const wasRecentSearch =
        flagForSuggestedOrRecentSearch ===
          resultsConfigurations.RecentSearchClick
          ? 'true'
          : 'false';
      // Calling the RecentSearch and Suggestion Click analytics usecase
      AnalyticsEvents.recentSearchAndSuggestionsClick(
        request.searchTerm,
        request.accounts,
        request.homepageSearch,
        wasRecentSearch,
        resultCount
      );
    } else {
      // Calling the Search Action analytics usecase
      AnalyticsEvents.searchAction(
        request.searchTerm,
        request.accounts,
        request.homepageSearch,
        request.vertical,
        resultCount
      );
    }
    if (
      request.vertical !== appConstants.verticals.People &&
      !request.clearAll
    ) {
      AnalyticsEvents.refinerAction(
        request.searchTerm,
        request.accounts,
        request.homepageSearch,
        request.filterByDate,
        request.refinersClicked,
        request.flagForContentAge,
        request.flagForClear,
        resultCount,
        request.vertical
      );
    } else if (
      request.vertical === appConstants.verticals.People &&
      request.refinersClicked &&
      request.refinersClicked.length > 0 &&
      !request.clearAll
    ) {
      AnalyticsEvents.refinerAction(
        request.searchTerm,
        request.accounts,
        request.homepageSearch,
        request.filterByDate,
        request.refinersClicked,
        request.flagForContentAge,
        request.flagForClear,
        resultCount,
        request.vertical
      );
    }
  };

  /**
   * Method to get best bets results
   * @param   {object} requestParams the request object containing search query term & an optional start row.
   * @return  {object} return api response
   */
  bestbets = createAsyncThunk(
    'bestBets',
    async (
      requestParams: {
        searchTerm: string;
        vertical: string;
        accounts: Array<Object>;
      },
      thunkApi
    ): Promise<any> => {
      let responseData: any = [];
      let modelObj = UtilityService.getRequestModel(requestParams.vertical);
      const searchConfig = await this.getSearchConfig(requestParams.searchTerm, requestParams.vertical);
      searchConfig.accountInfo = requestParams.accounts;
      searchConfig.data = {
        ...searchConfig.data,
        Query: requestParams.searchTerm,
        verticals: requestParams.vertical,
        Size: 3,
        Filters: {
          Condition: 'AND',
          Rules: []
        },
        ReturnFields: modelObj ? Object.keys(modelObj) : [],
        SearchType: appConstants.searchType.Keyword
      };
      let geographyFiltersBestBets: string[] = [];
      let userInfo = null;
      try {
        userInfo = sessionStorage.UserInfo
          ? JSON.parse(sessionStorage.UserInfo)
          : null;
      } catch (parseError) {
        userInfo = null;
      }

      if (userInfo && userInfo.EYCountry) {

        const path = UtilityService.findGeographyPath(
          GeographyData.children,
          userInfo.EYCountry,
          []
        );

        if (path) {
          geographyFiltersBestBets.push(...[...path].reverse());
        } else {
          geographyFiltersBestBets.push(userInfo.EYCountry);
        }
        if (geographyFiltersBestBets.indexOf(userInfo.EYCountry) === -1) {
          geographyFiltersBestBets.push(userInfo.EYCountry);
        }
      }
      geographyFiltersBestBets.push('Global');
      if (geographyFiltersBestBets.length > 1) {
        searchConfig.data.Filters.Rules.push({
          Field: 'ESC_GEOGRAPHICAPPLICABILITY',
          Type: 'String',
          Operator: ':',
          Values: geographyFiltersBestBets
        });
        searchConfig.data.Filters.Rules.push({
          Field: 'ESC_GEOGRAPHICAPPLICABILITY_ALT_LABEL',
          Type: 'String',
          Operator: ':',
          Values: geographyFiltersBestBets
        });
      } else {
        searchConfig.data.Filters.Rules.push({
          Field: 'ESC_GEOGRAPHICAPPLICABILITY',
          Type: 'String',
          Operator: ':',
          Value: geographyFiltersBestBets[0]
        });
        searchConfig.data.Filters.Rules.push({
          Field: 'ESC_GEOGRAPHICAPPLICABILITY_ALT_LABEL',
          Type: 'String',
          Operator: ':',
          Value: geographyFiltersBestBets[0]
        });
      }
      await ApiService.requestApi([searchConfig])
        .then(
          (response: { [key: string]: any }) => {
            if (response && response[0] && response[0].result.relevantResults) {
              response[0].result.relevantResults.forEach((record: any) => {
                let federatorData = JSON.parse(record.FederatorMetadata);
                if (federatorData && federatorData.length > 0) {
                  let backendName = federatorData.find((obj: any) => {
                    return obj.Key === 'FederatorBackend';
                  }).Value;
                  if (
                    backendName === appConstants.backendDataSources.bestbets ||
                    backendName === appConstants.backendDataSources.bestbets_dev ||
                    backendName === appConstants.backendDataSources.bestbets_qa
                  ) {
                    record.vertical = appConstants.BestBets;
                  }
                  responseData.push(
                    UtilityService.getResponseModel(record.vertical, record)
                  );
                }
              });
            }
          },
          (error) => {
            throw new Error(error);
          }
        )
        .finally(() => {
          if (responseData && responseData.length > 0) {
            thunkApi.dispatch(setBestBetFlag(true));
          } else {
            thunkApi.dispatch(setBestBetFlag(false));
          }
        });

      return {
        bestBetsResults: responseData.sort((a: any, b: any) =>
          a.Title.localeCompare(b.Title)
        )
      };
    }
  );

  /**
   * Method to get Insight results
   * @param   {object} requestParams the request object containing search query term & an optional start row.
   * @return  {object} return api response
   */
  insights = createAsyncThunk(
    'insights',
    async (
      requestParams: {
        searchTerm: string;
        vertical: string;
        accounts: Array<Object>;
      },
      thunkApi
    ): Promise<any> => {
      let responseData: any = [];
      let modelObj = UtilityService.getRequestModel(requestParams.vertical);
      //  const searchConfig = { ...this.searchApiConfig };
      const searchConfig = {
        ...await this.getSearchConfig(
          requestParams.searchTerm,
          appConstants.InsightsData
        )
      };
      searchConfig.accountInfo = requestParams.accounts;
      searchConfig.data = {
        ...searchConfig.data,
        Query: requestParams.searchTerm,
        verticals: 'InsightsPane',
        Filters: {
          Condition: 'OR',
          Rules: [
            {
              Field: 'contentType',
              Type: 'String',
              Operator: ':',
              Values: [
                'EY Knowledge Buyer Page',
                'EY Knowledge FoP Page',
                'EY Knowledge Solution Page',
                'EY Topic Page'
              ]
            }
          ]
        },
        Size: 1,
        ReturnFields: modelObj ? Object.keys(modelObj) : []
      };

      await ApiService.requestApi([searchConfig])
        .then(
          (response: { [key: string]: any }) => {
            if (response && response[0] && response[0].result.relevantResults) {
              response[0].result.relevantResults.forEach((record: any) => {
                responseData.push(
                  UtilityService.getResponseModel(record.vertical, record)
                );
              });
            }

            let peopleEmailIds: Array<string | undefined> = [];
            let contentType: string =
              response[0] && responseData[0].contentType;

            switch (contentType) {
              case cardConfig.contentTypes.EYKnowledgeFoPPage:
              case cardConfig.contentTypes.EYKnowledgeSolutionPage:
                peopleEmailIds =
                  responseData[0].solutionLeadersEmail.split(';');
                break;

              case cardConfig.contentTypes.EYKnowledgeBuyerPage:
              case cardConfig.contentTypes.EYTopicPage:
                peopleEmailIds = responseData[0].eyContactEmail.split(';');
                break;

              default:
                break;
            }

            // Graph API call for Insights leads data fetch
            const validPeopleEmails = peopleEmailIds.filter((id) => id);
            if (validPeopleEmails.length) {
              thunkApi.dispatch(
                this.getInsightLeadsFromGraph({
                  peopleEmails: validPeopleEmails,
                  accounts: requestParams.accounts
                })
              );

              thunkApi.dispatch(
                this.getInsightLeadsPhotosFromGraph({
                  peopleEmails: validPeopleEmails,
                  accounts: requestParams.accounts
                })
              );
            } else {
              thunkApi.dispatch(clearInsightsLeads());
            }
          },
          (error) => {
            throw new Error(error);
          }
        )
        .finally(() => {
          if (responseData && responseData.length > 0) {
            thunkApi.dispatch(setInsightsFlag(true));
          } else {
            thunkApi.dispatch(setInsightsFlag(false));
          }
        });

      return {
        insightResults: responseData
      };
    }
  );

  /**
   * Method to get Insight Leads data from graph API
   * @param   {object} requestParams the request object containing leadsemails.
   * @return  {object} return api response
   */
  public getInsightLeadsFromGraph = createAsyncThunk(
    'fetchLeadsData',
    async (requestParams: {
      peopleEmails: Array<string | undefined>;
      accounts: Array<Object>;
    }): Promise<any> => {
      let graphApiConfigArr: any = [];
      const batchSize = 20;
      for (
        let batchIndex = 0;
        batchIndex < requestParams.peopleEmails.length;
        batchIndex += batchSize
      ) {
        const chunkPeopleAccountEmails = requestParams.peopleEmails.slice(
          batchIndex,
          batchIndex + batchSize
        );
        let insightsLeadBatchCallObject =
          this.getLeadProfileBatchCol(chunkPeopleAccountEmails);
        graphApiConfigArr.push({
          ...apiConfig.getProfilePicture,
          data: insightsLeadBatchCallObject,
          accountInfo: requestParams.accounts
        });
      }

      return ApiService.requestApi(graphApiConfigArr);
    }
  );

  /**
   * Method to get Insight Leads photos from graph API
   * @param   {object} requestParams the request object containing leadsemails.
   * @return  {object} return api response
   */
  public getInsightLeadsPhotosFromGraph = createAsyncThunk(
    'fetchLeadsPhotos',
    async (requestParams: {
      peopleEmails: Array<string | undefined>;
      accounts: Array<Object>;
    }): Promise<any> => {
      let graphApiConfigArr: any = [];
      const batchSize = 20;
      for (
        let batchIndex = 0;
        batchIndex < requestParams.peopleEmails.length;
        batchIndex += batchSize
      ) {
        const chunkPeopleAccountEmails = requestParams.peopleEmails.slice(
          batchIndex,
          batchIndex + batchSize
        );
        let insightsLeadBatchCallObject =
          this.getLeadsProfilePhotoBatchCol(chunkPeopleAccountEmails);
        graphApiConfigArr.push({
          ...apiConfig.getProfilePicture,
          data: insightsLeadBatchCallObject,
          accountInfo: requestParams.accounts
        });
      }

      return ApiService.requestApi(graphApiConfigArr);
    }
  );

  getLeadProfileBatchCol = (leadEmails: any) => {
    const postDataPhoto = leadEmails.map((leadEmail: string) => ({
      id: leadEmail,
      method: 'GET',
      url: `/users/${leadEmail}?$select=displayName,mail,jobTitle,id`
    }));

    return { requests: postDataPhoto };
  };

  getLeadsProfilePhotoBatchCol = (leadEmails: any) => {
    const postDataPhoto = leadEmails.map((leadEmail: string) => ({
      id: leadEmail,
      method: 'GET',
      url: `/users/${leadEmail}/photo/$value`
    }));

    return { requests: postDataPhoto };
  };

  public getUserProfilePic = createAsyncThunk(
    'userProfilePhotos',
    async (requestParams: {
      peopleAccountIds: Array<string | undefined>;
      accounts: Array<Object>;
    }): Promise<any> => {
      let graphApiConfigArr: any = [];
      const batchSize = 20;
      for (
        let batchIndex = 0;
        batchIndex < requestParams.peopleAccountIds.length;
        batchIndex += batchSize
      ) {
        const chunkPeopleAccountIds = requestParams.peopleAccountIds.slice(
          batchIndex,
          batchIndex + batchSize
        );
        let peopleProfileBatchCallObject = this.getPeopleProfileBatchCol(
          chunkPeopleAccountIds
        );
        graphApiConfigArr.push({
          ...apiConfig.getProfilePicture,
          data: peopleProfileBatchCallObject,
          accountInfo: requestParams.accounts
        });
      }

      return ApiService.requestApi(graphApiConfigArr);
    }
  );

  getPeopleProfileBatchCol = (peopleAccountIds: any) => {
    const postDataPhoto = peopleAccountIds.map((peopleAccountId: string) => ({
      id: peopleAccountId,
      method: 'GET',
      url: `/users/${peopleAccountId}/photo/$value`
    }));

    return { requests: postDataPhoto };
  };

  /**
   * async method to trigger suggestions search action
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {string} searchTerm  search query term
   */
  queryRecommondations = createAsyncThunk(
    'queryRecommondations',
    async (request: { searchTerms: string; visibleSuggestions: number }) => {
      const suggestionsConfig = { ...apiConfig.querySuggestions };
      suggestionsConfig.data.Query = request.searchTerms;
      let responseData: any = [];
      await ApiService.requestApi([suggestionsConfig])
        .then((suggestionsArray: any) => {
          if (suggestionsArray.length > 0) {
            suggestionsArray[0].map((item: any) => {
              responseData.push(item);
            });
          }
          if (responseData.length > 0) {
            responseData.push(`Search for ${request.searchTerms}`);
          }
        })
        .catch((error) => {
          if (isLocalOrDevEnv) {
            console.log('Error' + error);
          }
        });

      return {
        suggestionList: responseData
      };
    }
  );
  /** 
   * Method to fetch the Ontology Response 
   * We are using the Ontology API to fetch the Ontology Response and parsing through it to obtain all
   the object labels and storing it in an array.
   *  @param   {object} request the request object containing search query term */

  fetchOntologyData = createAsyncThunk(
    'fetchOntologyData',
    async (request: { searchTerm: string }) => {
      const ontologyConfig = apiConfig.getOntologyData;
      ontologyConfig.baseUrl = envConfig.ontology.BaseUrl;
      ontologyConfig.data.searchText = request.searchTerm;
      const ontologyResponse: any = await ApiService.requestApi([
        ontologyConfig
      ]);

      let objectLabelsArray: any[] = [];

      ontologyResponse.map((result: any) => {
        result.result.concepts.map((concept: any) => {
          if (concept.label && concept.label.value) {
            objectLabelsArray.push(concept.label.value);
          }
        });
      });

      return {
        ontologyList: objectLabelsArray
      };
    }
  );

  /**
   * Method to trigger API endpoint to get Preview Stats
   * this is used to access the preview stats based on api endpoint configuration and
   * add dynamic params in the same fetch.
   * @param  {string} aemId AEM Id to get the Preview Stats for specific item
   */
  public getPreviewStats = createAsyncThunk(
    'getPreviewStats',
    async (request: { aemId: string }) => {
      const userDetails = store.getState().general;
      let responseData: any = [];

      await fetch(
        envConfig.aemPreviewStats.replaceAll('$eyId', request.aemId),
        {
          headers: {
            Authorization: 'Bearer ' + userDetails.token
          }
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error('Request failed');
          }

          return response.json();
        })
        .then((data) => {
          responseData = [...responseData, data];
        })
        .catch((error) => {
          if (isLocalOrDevEnv) {
            console.log('Error in Preview Stats: ' + error);
          }
        });

      return {
        previewStats: responseData[0]
      };
    }
  );

  // Fetch user preferences to get initial searchMode 

  public fetchUserPreferences = createAsyncThunk<{ searchMode: 'Keyword' | 'Semantic' }, { accounts: Array<Object> }>(
    'results/fetchUserPreferences',
    async (request) => {
      // If toggle AI search feature is disabled, always return Keyword
      if (!envConfig.isToggleAISearchEnabled) {
        return { searchMode: 'Keyword' as const };
      }
      try {
        const userPreferencesConfig = { ...apiConfig.getUserProfileProperties };
        userPreferencesConfig.accountInfo = request.accounts;
        const response = await ApiService.requestApi([userPreferencesConfig]);
        if (response && response[0]) {
          const preferences = response[0] as any;
          const newSearchMode = preferences.enableAISearch ? 'Semantic' as const : 'Keyword' as const;
          if (isLocalOrDevEnv) {
            console.log('Initial searchMode set from API:', newSearchMode, '(enableAISearch:', preferences.enableAISearch, ')');
          }

          return { searchMode: newSearchMode };
        }

        return { searchMode: 'Keyword' as const };
      } catch (error) {
        if (isLocalOrDevEnv) {
          console.warn('Failed to fetch user preferences on load:', error);
        }

        return { searchMode: 'Keyword' as const };
      }
    }
  );

  // Update user's search preference for AI Search and persist it through API call

  public updateToggleAISearch = createAsyncThunk(
    'results/updateToggleAISearch',
    async (request: { accounts: Array<Object>; enableAISearch: boolean }) => {
      try {
        const toggleConfig = { ...apiConfig.toggleAISearch };
        toggleConfig.accountInfo = request.accounts;
        toggleConfig.data = { enableAISearch: request.enableAISearch };
        await ApiService.requestApi([toggleConfig]);
      } catch (error) {
        console.warn('Failed to update AI search preference:', error);
      }
    }
  );
}

const SearchUseCase = new Search();
export default SearchUseCase;
